// Shared unread-badge selectors — the ONE place that combines the three raw
// unread stores into "does this entity badge, and by how much?" (B1/B2/B3).
//
// Before this module every surface (rail, dock, sidebar rows, project rollup,
// mobile, switcher) hand-rolled its own combination of:
//   • notificationState  — the RED mention/DM COUNT
//   • unreadFlagState    — the BOLD "has any unread" flag
//   • notifPrefsState    — the per-scope mute / mentions-only preference
// …so mute was AND-ed in only some of them (B1), agent rows queried the raw
// per-session id instead of the canonical cybo id the server keys unread on
// (B2), and the rail count vs the project rollup flag disagreed (B3).
//
// All three stores stay unchanged: mute is applied at READ time here, so a
// mute/unmute is reactive with no reseed. Every surface now routes through
// these functions, so they cannot drift from each other again.
import { canonicalAgentDmPeerId } from "../cybo-dm-peer-id.js";
import { orphanAgentDmPeerIds } from "../agent-dm-surface.js";
import { isSessionSilenced } from "../session-notification-scope.js";
import { cyboState, activityState } from "../plugins/agents/state.svelte.js";
import {
  authState,
  notificationState,
  unreadFlagState,
  notifPrefsState,
  workspaceState,
  dmActivityState,
  isArchivedForMe,
} from "../core/state.svelte.js";

// Resolve a row/URL id to the id the server keys unread on: an agent's live
// session id → its stable cyboId; channels / human DMs pass through unchanged
// (they're never in the agents list). Idempotent — a value that's already
// canonical (e.g. a stored count key) maps to itself.
function canon(id: string): string {
  return canonicalAgentDmPeerId(workspaceState.agents, id);
}

// The effective preference for an id, resolved on the CANONICAL id (the count
// map's key). Only channel scopes (incl. group DMs) carry a pref: the server no
// longer returns per-person DM rows, so a human/agent DM resolves to "all".
function pref(id: string): string {
  return notifPrefsState.get(canon(id));
}

// T-75: the person set this Sessions row (canonical peer) to No notifications.
function silenced(id: string): boolean {
  return isSessionSilenced(notifPrefsState.prefs, canon(id));
}

// The RED number for an entity: its mention/DM count, or 0 when muted, a silenced session or
// archived for me (a channel hidden from my sidebar must not badge anywhere; it counts again once
// it resurfaces).
export function badgeCount(workspaceId: string, id: string): number {
  return pref(id) === "muted" || silenced(id) || isArchivedForMe(id)
    ? 0
    : notificationState.getCount(workspaceId, canon(id));
}

// Does a Sessions / agent DM row go BOLD? On any unread message, a silenced session included
// (bold, no count: the mentions-only look).
export function boldSessionRow(workspaceId: string, id: string): boolean {
  if (pref(id) === "muted" || isArchivedForMe(id)) return false;
  return notificationState.getCount(workspaceId, canon(id)) > 0;
}

// How many unread messages a channel has, for its number in the sidebar: 0 when muted,
// archived, mentions-only (only mentions count there) or read.
export function channelUnreadCount(workspaceId: string, id: string): number {
  const p = pref(id);
  if (p === "muted" || p === "mentions_only" || isArchivedForMe(id)) return 0;
  return unreadFlagState.unreadCount(workspaceId, canon(id));
}

// Last-activity timestamp for an entity (0 = never), on the CANONICAL id — the
// recency twin of badgeCount, and the same B2 fix. Writes land under the stable
// cybo id (the `dm` handler canonicalises before it bumps), while agent rows and
// the sessions list hold a per-summon session id, so a raw read returned 0
// forever and those rows sorted as "never active". Channels and human DMs are
// never in the agents list, so canon() passes them through unchanged.
export function dmRecency(workspaceId: string, id: string): number {
  return dmActivityState.getActivity(workspaceId, canon(id));
}

// Does this entity show ANY unread indicator (red count OR bold flag)?
//   muted         → never.
//   mentions_only → only the count (bold flag suppressed).
//   all           → count OR bold flag.
export function hasBadge(workspaceId: string, id: string): boolean {
  const p = pref(id);
  if (p === "muted" || silenced(id) || isArchivedForMe(id)) return false;
  const c = canon(id);
  const count = notificationState.getCount(workspaceId, c);
  if (p === "mentions_only") return count > 0;
  return count > 0 || unreadFlagState.isUnread(workspaceId, c);
}

// Does a channel row go BOLD? Any unread (flag or mention) on a channel that is neither muted nor
// archived, whatever its pref: mentions_only keeps its red NUMBER to mentions (badgeCount) but
// still bolds on any new message, like Slack. hasBadge stays the dock / count rule.
export function boldChannel(workspaceId: string, id: string): boolean {
  if (pref(id) === "muted" || isArchivedForMe(id)) return false;
  const c = canon(id);
  return notificationState.getCount(workspaceId, c) > 0 || unreadFlagState.isUnread(workspaceId, c);
}

// Every roster a row is built from has answered, so a key without a row is real.
export function unreadRostersSettled(): boolean {
  return (
    !workspaceState.membersLoading &&
    !workspaceState.membersLoadError &&
    workspaceState.members.length > 0 &&
    !workspaceState.agentsLoading &&
    !cyboState.loading &&
    !cyboState.loadError
  );
}

// A badged agent DM no roster row reads and Activity does not host (past the orphan backfill's
// fetch cap, or a session on a daemon that is not attached): the viewer can never open or clear
// it, so the totals leave it out. The invariant beacon still reports it. Only the open workspace
// has rosters to judge by.
export function doorlessKeys(workspaceId: string): ReadonlySet<string> {
  if (workspaceId !== workspaceState.current?.id || !unreadRostersSettled()) return new Set();
  return new Set(
    orphanAgentDmPeerIds(
      notificationState.countsForWorkspace(workspaceId),
      workspaceState.channels.map((c) => c.id),
      workspaceState.members.map((m) => m.userId),
      cyboState.list.map((c) => c.id),
      workspaceState.agents,
      authState.user,
    ).filter(
      (key) =>
        !activityState.items.some((i) => i.actorType === "agent" && i.sourceId === `dm:${key}`),
    ),
  );
}

// Σ of every NON-muted count in a workspace (the rail "Chat" badge). Count keys
// are already canonical, so pref() on each key is a no-op canon.
export function workspaceBadgeTotal(workspaceId: string): number {
  const doorless = doorlessKeys(workspaceId);
  let total = 0;
  for (const [id, n] of Object.entries(notificationState.countsForWorkspace(workspaceId))) {
    if (pref(id) !== "muted" && !silenced(id) && !isArchivedForMe(id) && !doorless.has(id))
      total += n;
  }
  return total;
}

// Global rollups count only workspaces the viewer belongs to (plus the open one): a
// count left in a workspace outside the list has no surface to clear it. Until sign-in
// has loaded the list (an empty list included), everything counts.
function memberWorkspaces(ids: Iterable<string>): string[] {
  const all = [...ids];
  if (!workspaceState.listLoaded) return all;
  const mine = new Set(workspaceState.list.map((w) => w.id));
  const open = workspaceState.current?.id;
  return all.filter((id) => mine.has(id) || id === open);
}

// Σ across the viewer's workspaces (dock/taskbar badge + web tab title count).
export function globalBadgeTotal(): number {
  let total = 0;
  for (const ws of memberWorkspaces(notificationState.workspaceIds)) {
    total += workspaceBadgeTotal(ws);
  }
  return total;
}

// Is there ANY non-muted unread in a workspace the viewer belongs to (drives the
// dock "dot")? Unions the count-bearing and flag-bearing ids per workspace so a
// bold-only channel still counts under the "all" policy.
export function anyUnread(): boolean {
  const workspaces = memberWorkspaces(
    new Set<string>([...notificationState.workspaceIds, ...unreadFlagState.workspaceIds]),
  );
  for (const ws of workspaces) {
    const doorless = doorlessKeys(ws);
    const ids = new Set<string>([
      ...Object.keys(notificationState.countsForWorkspace(ws)),
      ...unreadFlagState.idsForWorkspace(ws),
    ]);
    for (const id of ids) {
      if (!doorless.has(id) && hasBadge(ws, id)) return true;
    }
  }
  return false;
}
